import { Form } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { GiftTtlField } from "@/components/brand/settings/products/gift-ttl-field"
import { Button } from "@/components/ui/button"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { brandSettingsProductVariantGiftTtlPath } from "@/routes"
import type { ProductRow, ProductVariantRow } from "@/types/products"

import { formatPrice } from "./format-price"

/**
 * OKS-931 — Variants expand IN PLACE (PRD D2).
 *
 * No detail page and no modal: a drill-in is a route + controller + policy +
 * a second empty state for data already on the page, and OKS-932 will want
 * that route for editing — so building it now for a read-only view means
 * OKS-932 rebuilds it.
 *
 * ⚠️ `CollapsibleTrigger` is Base UI, so it takes a **`render` prop, not
 * `asChild`** (UX §3.4). The shipped `AlertDialogTrigger` in
 * `shopify-integration-card.tsx` is the in-repo precedent. First-party Radix is
 * removed and a zero-radix guard reds on any Radix import.
 *
 * ⚠️ `CollapsibleContent` renders a plain `<ul>`, never a nested `<Table>`: a
 * table inside a table cell is a screen-reader hazard and breaks the column
 * widths above it.
 */
export default function ProductVariants({
  product,
  currency,
  canManage = false,
  errors,
}: {
  product: ProductRow
  currency: string
  /**
   * OKS-935 — gates the inline TTL editor. The server refusal is the guard
   * (`ProductPolicy#manage?` → 404); this only removes the affordance, so a
   * read-only member sees the variant line exactly as before.
   */
  canManage?: boolean
  /** Inertia render-prop errors, keyed `gift_ttl_days` after a failed PATCH. */
  errors?: Record<string, string | string[] | undefined>
}) {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState(false)

  /*
    OKS-932 UX §2.1.5 — a disclosure control with nothing to disclose is a
    dead affordance, and a manual product with no variants is now the common
    case (D5 makes variants optional). Render the count as static muted text
    instead of a trigger.

    This changes behaviour for SYNCED products too — a synced product with
    zero variants in the current view previously got a clickable trigger that
    opened an empty panel. That is a deliberate, flagged improvement to a
    shipped surface (UX §9.5), not an accident of the manual work.
  */
  if (product.variant_count === 0) {
    return (
      <span
        className="text-muted-foreground text-sm"
        data-testid="brand-product-variants-empty"
      >
        {/*
          `no_variants` — the same key the old empty panel used, kept alive
          here deliberately. It is the string UX §2.1.5 names, and dropping it
          for `variant_count {count: 0}` would orphan the key, which
          `oks870_unused_key_guard_spec` reds on.
        */}
        {t("settings.brand.products.no_variants")}
      </span>
    )
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger
        render={
          <Button
            variant="ghost"
            size="sm"
            data-testid="brand-product-variants-toggle"
            /* Visible label is the count; the aria-label names the ACTION. */
            aria-label={t(
              open
                ? "settings.brand.products.variants_toggle_hide"
                : "settings.brand.products.variants_toggle_show",
            )}
          >
            {t("settings.brand.products.variant_count", {
              count: product.variant_count,
            })}
          </Button>
        }
      />
      <CollapsibleContent>
        <ul className="space-y-1 py-2" data-testid="brand-product-variant-list">
          {product.variants.map((variant) => (
            <VariantLine
              /*
                ⚠️ `variant.id`, NEVER `shopify_variant_id` — that is null on
                every manual row, and `key={null}` is *provided* (React
                stringifies it to "null") rather than falling back to the
                index, so two manual variants would share one key.
              */
              key={variant.id}
              variant={variant}
              productId={product.id}
              canManage={canManage}
              errors={errors}
              currency={currency}
              language={i18n.language}
            />
          ))}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  )
}

function VariantLine({
  variant,
  productId,
  canManage,
  errors,
  currency,
  language,
}: {
  variant: ProductVariantRow
  productId: number
  canManage: boolean
  errors?: Record<string, string | string[] | undefined>
  currency: string
  language: string
}) {
  const { t } = useTranslation()
  const price = formatPrice(variant.price_cents, currency, language)

  return (
    <li
      className="text-muted-foreground flex flex-wrap gap-x-2 gap-y-1 text-sm"
      data-testid="brand-product-variant"
    >
      <span className="text-foreground">{variant.title}</span>
      <span>·</span>
      <span>
        {variant.sku
          ? t("settings.brand.products.variant.sku", { sku: variant.sku })
          : t("settings.brand.products.variant.no_sku")}
      </span>
      {price !== null ? (
        <>
          <span>·</span>
          <span className="tabular-nums">{price}</span>
        </>
      ) : null}
      <span>·</span>
      <span>
        {t(
          variant.available
            ? "settings.brand.products.variant.available"
            : "settings.brand.products.variant.unavailable",
        )}
      </span>
      {/*
        OKS-935 S1 — inline per-variant TTL (AC1).

        ⚠️ ONE variant per PATCH, addressed by id. There is deliberately no
        endpoint that takes a SET of variants: the Produits table paginates at
        25 and `serialize_product` filters variants to match the view, so this
        list structurally cannot see every variant. A set-replace would silently
        blank the rows it cannot show. Rows out of view are simply untouched and
        inherit the product-level TTL (PRD D15/D16).

        ⚠️ `basis-full` — `VariantLine` is already `flex-wrap`, so the control
        takes its own wrapped row at 375px rather than squeezing the price and
        availability off the line (UX §2).
      */}
      {canManage && (
        <Form
          action={brandSettingsProductVariantGiftTtlPath(productId, variant.id)}
          method="patch"
          options={{ preserveScroll: true }}
          className="basis-full"
          data-testid={`brand-product-variant-ttl-form-${variant.id}`}
        >
          {({ submit }) => (
            <GiftTtlField
              id={`variant-ttl-${variant.id}`}
              name="gift_ttl[gift_ttl_days]"
              value={variant.gift_ttl_days}
              error={errors?.gift_ttl_days}
              testId={`brand-product-variant-ttl-${variant.id}`}
              /*
                Commit on blur, and ONLY when the value actually changed —
                otherwise every focus traversal fires a PATCH. An explicit save
                button per row would be 25×N controls on a paginated table.
              */
              onCommit={(event) => {
                const next = event.currentTarget.value.trim()
                const current =
                  variant.gift_ttl_days === null
                    ? ""
                    : String(variant.gift_ttl_days)
                if (next !== current) submit()
              }}
            />
          )}
        </Form>
      )}
    </li>
  )
}
